import { ReadonlyVec3, vec3 } from "gl-matrix";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { Canvas2D } from "components";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import * as engine2D from "features/engine2D";
import { Picker, selectPicker } from "features/render";
import { selectIsProcoreDesign } from "slices/explorer/selectors";

import { selectHoveredMeasureEntity } from "./measureSlice";

const fillColor = "rgba(0, 170, 200, 0.3)";
const defaultLineColor = "rgba(255, 165, 0, 1)";

const pickerStyles: Partial<Record<Picker, { lineColor: string; pixelWidth: number }>> = {
    [Picker.Coordinates]: { lineColor: "#00DB2C", pixelWidth: 3 },
    [Picker.PointLine]: { lineColor: "#00DB2C", pixelWidth: 3 },
    [Picker.Dimension]: { lineColor: "#00DB2C", pixelWidth: 3 },
};

export function HoverCanvas({
    renderFnRef,
}: {
    renderFnRef: MutableRefObject<((moved: boolean) => void) | undefined>;
}) {
    const {
        state: { view, size },
    } = useExplorerGlobals();
    const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
    const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
    const hoveredEntity = useAppSelector(selectHoveredMeasureEntity);
    const picker = useAppSelector(selectPicker);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const useDashForGeneratedLines = picker === Picker.Measurement && !isProcoreDesign;

    const drawId = useRef(0);
    const draw = useCallback(async () => {
        const measure = view?.measure;
        if (!measure || !ctx || !canvas) {
            return;
        }

        const id = ++drawId.current;

        const drawVertexPoint = picker !== Picker.Coordinates && picker !== Picker.PointLine;
        const drawCornerEdges = picker === Picker.Coordinates || picker === Picker.PointLine;

        const getHoverDrawEntity = () => {
            if (!hoveredEntity) {
                return;
            }
            if (!drawVertexPoint && hoveredEntity.drawKind === "vertex") {
                return;
            }
            return measure.draw.getDrawEntity(hoveredEntity);
        };

        const getCornerEdges = () => {
            if (hoveredEntity?.drawKind !== "vertex") {
                return;
            }
            if (!drawCornerEdges) {
                return;
            }
            return measure.core.getVertexEdges(hoveredEntity);
        };

        const getIsGenerated = () => {
            if (!hoveredEntity) {
                return;
            }
            return measure.core.isParametricDataGenerated(hoveredEntity.ObjectId);
        };

        const [drawEntity, vertexEdges, isParametricDataGenerated] = await Promise.all([
            getHoverDrawEntity(),
            getCornerEdges(),
            getIsGenerated(),
        ]);

        if (id !== drawId.current) {
            return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!hoveredEntity) {
            return;
        }

        const cameraState = engine2D.getCameraState(view.renderState.camera);

        const { lineColor, pixelWidth } = pickerStyles[picker] || {
            lineColor: defaultLineColor,
            pixelWidth: 5,
        };

        if (drawEntity) {
            engine2D.drawProduct(
                ctx,
                cameraState,
                drawEntity,
                {
                    lineColor,
                    fillColor: fillColor,
                    pointColor: lineColor,
                    lineDash: isParametricDataGenerated && useDashForGeneratedLines ? [10, 10] : undefined,
                },
                pixelWidth,
            );
        }

        if (vertexEdges) {
            const point = hoveredEntity.parameter as ReadonlyVec3;
            for (const edge of vertexEdges) {
                if (edge.kind !== "line") {
                    continue;
                }
                const isStartVertex = vec3.dist(edge.start, point) < 1e-6;
                const isEndVertex = vec3.dist(edge.end, point) < 1e-6;
                if (!isStartVertex && !isEndVertex) {
                    continue;
                }
                const otherPoint = isStartVertex ? edge.end : edge.start;

                const drawProduct = view.measure?.draw.getDrawObjectFromPoints([point, otherPoint], {
                    closed: false,
                    angles: true,
                    generateLengthLabels: true,
                });

                if (!drawProduct) {
                    continue;
                }

                engine2D.drawProduct(ctx, cameraState, drawProduct, { lineColor }, pixelWidth);
            }
        }
    }, [view, ctx, canvas, hoveredEntity, picker, useDashForGeneratedLines]);

    useEffect(() => {
        draw();
    }, [draw, size]);

    useEffect(() => {
        renderFnRef.current = animate;
        return () => (renderFnRef.current = undefined);

        async function animate(moved: boolean): Promise<void> {
            if (!view || !moved) {
                return;
            }

            draw();
        }
    }, [view, renderFnRef, draw]);

    const canDraw =
        hoveredEntity &&
        [Picker.Area, Picker.Measurement, Picker.PointLine, Picker.Coordinates, Picker.Dimension].includes(picker);
    return (
        <>
            {canDraw && (
                <Canvas2D
                    id="hover-canvas"
                    data-include-snapshot
                    ref={(el) => {
                        setCanvas(el);
                        setCtx(el?.getContext("2d") ?? null);
                    }}
                    width={size.width}
                    height={size.height}
                />
            )}
        </>
    );
}
